import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileSearch,
  FileText,
  Package,
  Receipt,
  Search,
  SkipForward,
  StickyNote,
  UserPlus,
  Wrench,
  X,
} from 'lucide-react-native';
import { ComponentType, useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { InfiniteFooter } from '@/components/InfiniteFooter';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { useInfiniteWindow } from '@/hooks/use-infinite-window';
import { goBackOrTo } from '@/lib/nav';
import { webNoOutline } from '@/lib/web-style';
import { getActiveProperty } from '@/lib/tasks';
import {
  formatTimelineDate,
  groupByMonth,
  loadTimeline,
  type TimelineEntry,
  type TimelineKind,
} from '@/lib/timeline';

type FilterKey = 'all' | TimelineKind;

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'completion', label: 'Done' },
  { key: 'skip', label: 'Skipped' },
  { key: 'expense', label: 'Expenses' },
  { key: 'system', label: 'Systems' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'document', label: 'Docs' },
  { key: 'contact', label: 'Contacts' },
  { key: 'note', label: 'Notes' },
  { key: 'quote', label: 'Quotes' },
];

const KIND_META: Record<
  TimelineKind,
  { Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>; tint: string; bg: string; tag: string }
> = {
  completion: { Icon: CheckCircle2, tint: Palette.forest, bg: '#DCE7E1', tag: 'Done' },
  skip: { Icon: SkipForward, tint: Palette.clay, bg: 'rgba(176,95,60,0.13)', tag: 'Skipped' },
  expense: { Icon: Receipt, tint: Palette.honeyDeep, bg: 'rgba(225,163,61,0.18)', tag: 'Expense' },
  system: { Icon: Wrench, tint: Palette.forest, bg: '#EAF0EA', tag: 'System' },
  inventory: { Icon: Package, tint: Palette.honeyDeep, bg: 'rgba(225,163,61,0.14)', tag: 'Inventory' },
  document: { Icon: FileText, tint: Palette.clay, bg: 'rgba(176,95,60,0.13)', tag: 'Document' },
  contact: { Icon: UserPlus, tint: Palette.forest, bg: '#EAF0EA', tag: 'Contact' },
  note: { Icon: StickyNote, tint: Palette.honeyDeep, bg: Palette.honeySoft, tag: 'Note' },
  quote: { Icon: FileSearch, tint: Palette.forestGlow, bg: '#DCE7E1', tag: 'Quote' },
};

export default function Timeline() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [entries, setEntries] = useState<TimelineEntry[]>([]);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const prop = await getActiveProperty(db);
      if (!prop) {
        setEntries([]);
        setLoading(false);
        return;
      }
      const list = await loadTimeline(db, prop.id, 300);
      setEntries(list);
      setLoading(false);
    })();
  }, [db]);

  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries.filter((e) => {
      if (filter !== 'all' && e.kind !== filter) return false;
      if (!q) return true;
      const hay = `${e.title ?? ''} ${e.subtitle ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [entries, filter, query]);

  // Window the entries (not the groups) so the user genuinely "loads
  // more" instead of jumping a month at a time. We then regroup the
  // visible slice — that keeps the monthly section headers correct on
  // partial loads. Search & filter still operate on the full
  // `entries` set above, so nothing gets hidden from the filter pass.
  const win = useInfiniteWindow(filtered, { pageSize: 25, initialSize: 20 });
  const groups = useMemo(() => groupByMonth(win.visible), [win.visible]);

  const totalSpend = useMemo(
    () => filtered.reduce((sum, e) => sum + (e.amount_cents ?? 0), 0),
    [filtered]
  );

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/(tabs)')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Timeline</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView
        contentContainerStyle={{ paddingBottom: 80 }}
        showsVerticalScrollIndicator={false}
        onScroll={win.onScroll}
        onLayout={win.onLayout}
        scrollEventThrottle={16}>
        <View style={styles.hero}>
          <Text style={styles.heroTitle}>Everything your home remembers</Text>
          <Text style={styles.heroSub}>
            Tasks finished, expenses paid, systems added, documents and contacts —
            one chronological story.
          </Text>
          <View style={styles.heroStats}>
            <Stat label="Entries" value={String(filtered.length)} />
            <Stat
              label={filter === 'expense' ? 'Total paid' : 'Tracked $'}
              value={totalSpend > 0 ? formatMoney(totalSpend) : '—'}
            />
          </View>
        </View>

        <View style={styles.searchWrap}>
          <View style={styles.searchBox}>
            <Search size={15} color={Palette.inkSoft} strokeWidth={2} />
            <TextInput
              value={query}
              onChangeText={setQuery}
              placeholder="Search by task, vendor, item…"
              placeholderTextColor={Palette.inkFaint}
              autoCorrect={false}
              style={[styles.searchInput, webNoOutline]}
            />
            {query ? (
              <Pressable onPress={() => setQuery('')} hitSlop={8}>
                <X size={14} color={Palette.inkFaint} strokeWidth={2} />
              </Pressable>
            ) : null}
          </View>
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.chipRow}>
          {FILTERS.map((f) => {
            const sel = filter === f.key;
            return (
              <Pressable
                key={f.key}
                onPress={() => setFilter(f.key)}
                style={({ pressed }) => [styles.chip, sel && styles.chipSel, pressed && { opacity: 0.9 }]}>
                <Text style={[styles.chipText, sel && styles.chipTextSel]}>{f.label}</Text>
              </Pressable>
            );
          })}
        </ScrollView>

        {loading ? null : groups.length === 0 ? (
          <View style={styles.empty}>
            <Text style={styles.emptyTitle}>Nothing here yet</Text>
            <Text style={styles.emptyBody}>
              Mark a task done, log an expense, or add a system and it&apos;ll
              appear here.
            </Text>
          </View>
        ) : (
          <>
            {groups.map((g) => (
              <View key={g.label}>
                <Text style={styles.monthLabel}>{g.label.toUpperCase()}</Text>
                <View style={styles.card}>
                  {g.items.map((e, i) => (
                    <EntryRow
                      key={e.id}
                      entry={e}
                      onPress={() => navigateForEntry(router, e)}
                      last={i === g.items.length - 1}
                    />
                  ))}
                </View>
              </View>
            ))}
            <InfiniteFooter hasMore={win.hasMore} total={win.total} loadMore={win.loadMore} noun="entry" nounPlural="entries" />
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

function navigateForEntry(router: ReturnType<typeof useRouter>, e: TimelineEntry) {
  switch (e.kind) {
    case 'completion':
    case 'skip':
      // Both lead to the same task detail page — the kind only affects
      // how the row is rendered in the list (tint + icon + tag).
      router.push(`/task/${e.ref_id}` as never);
      break;
    case 'expense':
      router.push(`/expenses/${e.ref_id}` as never);
      break;
    case 'system':
      router.push(`/system/${e.ref_id}` as never);
      break;
    case 'inventory':
      router.push(`/inventory/${e.ref_id}` as never);
      break;
    case 'contact':
      router.push(`/contact/${e.ref_id}` as never);
      break;
    case 'document':
      router.push('/media' as never);
      break;
    case 'note':
      // Deep-link to the dedicated Notes page with the focused note
      // pre-opened in the editor. The page reads ?focus=<id> on mount
      // and auto-opens the matching note.
      router.push({ pathname: '/notes', params: { focus: e.ref_id } } as never);
      break;
    case 'quote':
      router.push(`/quotes/${e.ref_id}` as never);
      break;
  }
}

function EntryRow({
  entry,
  onPress,
  last,
}: {
  entry: TimelineEntry;
  onPress: () => void;
  last?: boolean;
}) {
  const meta = KIND_META[entry.kind];
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [styles.row, last && { borderBottomWidth: 0 }, pressed && { opacity: 0.85 }]}>
      <View style={[styles.rowIcon, { backgroundColor: meta.bg }]}>
        <meta.Icon size={18} color={meta.tint} strokeWidth={1.8} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={[
            styles.rowTitle,
            // Completed task entries get the same "rear-view" treatment
            // as the Past tasks list — faded + struck through.
            entry.kind === 'completion' && styles.rowTitleDone,
          ]}
          numberOfLines={2}>
          {entry.title || meta.tag}
        </Text>
        <Text style={styles.rowMeta} numberOfLines={1}>
          {formatTimelineDate(entry.at)}
          {entry.subtitle ? ` · ${entry.subtitle}` : ''}
        </Text>
      </View>
      {entry.amount_cents && entry.amount_cents > 0 ? (
        <Text style={styles.amount}>{formatMoney(entry.amount_cents)}</Text>
      ) : null}
      <ChevronRight size={16} color={Palette.inkFaint} strokeWidth={1.7} />
    </Pressable>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.stat}>
      <Text style={styles.statValue}>{value}</Text>
      <Text style={styles.statLabel}>{label.toUpperCase()}</Text>
    </View>
  );
}

function formatMoney(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  hero: {
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    backgroundColor: Palette.card, borderRadius: Radius.xl,
    borderWidth: 1, borderColor: Palette.line, padding: 18,
  },
  heroTitle: {
    fontFamily: Fonts.serif, fontSize: 22, color: Palette.ink, letterSpacing: -0.3,
  },
  heroSub: {
    marginTop: 6, fontSize: 13, lineHeight: 19,
    color: Palette.inkSoft, fontFamily: Fonts.sansMedium,
  },
  heroStats: {
    flexDirection: 'row', gap: 18, marginTop: Spacing.three,
  },
  stat: {},
  statValue: {
    fontFamily: Fonts.serifBold, fontSize: 24, color: Palette.ink, letterSpacing: -0.5,
  },
  statLabel: {
    marginTop: 2, fontSize: 11.5, fontFamily: Fonts.sansBold,
    color: Palette.inkFaint, letterSpacing: 0.6,
  },
  searchWrap: {
    marginTop: Spacing.three,
    paddingHorizontal: Spacing.four,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    fontFamily: Fonts.sansSemibold,
    fontSize: 14,
    color: Palette.ink,
    padding: 0,
  },
  chipRow: {
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.three,
    gap: 6,
  },
  chip: {
    paddingHorizontal: 12, paddingVertical: 7, borderRadius: 999,
    borderWidth: 1.5, borderColor: Palette.line, backgroundColor: Palette.card,
    marginRight: 6,
  },
  chipSel: { backgroundColor: Palette.forest, borderColor: Palette.forest },
  chipText: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.ink },
  chipTextSel: { color: Palette.cream },
  monthLabel: {
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkFaint,
    letterSpacing: 0.7, marginBottom: 8,
  },
  card: {
    marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, paddingHorizontal: 14,
  },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: Palette.line,
  },
  rowIcon: {
    width: 36, height: 36, borderRadius: 11,
    alignItems: 'center', justifyContent: 'center',
  },
  rowTitle: { fontSize: 13.5, fontFamily: Fonts.sansBold, color: Palette.ink },
  rowTitleDone: {
    textDecorationLine: 'line-through',
    textDecorationStyle: 'solid',
    textDecorationColor: Palette.inkSoft,
    color: Palette.inkSoft,
    opacity: 0.75,
  },
  rowMeta: {
    marginTop: 2, fontSize: 12.5, fontFamily: Fonts.sansMedium,
    color: Palette.inkSoft,
  },
  amount: {
    fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.ink,
  },
  empty: {
    marginTop: Spacing.five, paddingHorizontal: 32,
    alignItems: 'center',
  },
  emptyTitle: {
    fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink,
  },
  emptyBody: {
    marginTop: 6, fontSize: 13, lineHeight: 19,
    color: Palette.inkSoft, fontFamily: Fonts.sansMedium, textAlign: 'center',
  },
});
